import { useState, useEffect } from 'react'
import { useGameStore } from '@/store/gameState'
import {
  FOCUS_DURATIONS,
  GRACE_PERIOD_MINUTES,
  ENERGY_INTERVAL_MINUTES,
  TRAVEL_TIME_MINUTES,
} from '@/data/constants'
import { HUNTS } from '@/data/hunts'

interface TimerState {
  isActive: boolean
  duration: number
  elapsedMinutes: number
  remainingMinutes: number
  isComplete: boolean
  isInGrace: boolean
  isDecaying: boolean
  graceRemaining: number
  decay: number
  baseEnergy: number
  currentEnergy: number
  proRatedEnergy: number  // Energy earned if stopped early (based on 5-min intervals)
}

export function useTimer(): TimerState {
  const focusSession = useGameStore((s) => s.focusSession)
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    if (!focusSession) return

    const interval = setInterval(() => {
      setNow(Date.now())
    }, 1000)

    return () => clearInterval(interval)
  }, [focusSession])

  if (!focusSession) {
    return {
      isActive: false,
      duration: 0,
      elapsedMinutes: 0,
      remainingMinutes: 0,
      isComplete: false,
      isInGrace: false,
      isDecaying: false,
      graceRemaining: 0,
      decay: 0,
      baseEnergy: 0,
      currentEnergy: 0,
      proRatedEnergy: 0,
    }
  }

  const startTime = new Date(focusSession.startedAt).getTime()
  const elapsedMs = now - startTime
  const elapsedMinutes = elapsedMs / 60000
  const duration = focusSession.duration
  const baseEnergy = FOCUS_DURATIONS[duration]

  const isComplete = elapsedMinutes >= duration
  const overTime = isComplete ? elapsedMinutes - duration : 0
  const isInGrace = isComplete && overTime < GRACE_PERIOD_MINUTES
  const isDecaying = isComplete && overTime >= GRACE_PERIOD_MINUTES
  const graceRemaining = isInGrace ? GRACE_PERIOD_MINUTES - overTime : 0
  const decay = isDecaying ? Math.floor(overTime - GRACE_PERIOD_MINUTES) : 0
  const currentEnergy = Math.max(0, baseEnergy - decay)
  const remainingMinutes = Math.max(0, duration - elapsedMinutes)

  // Pro-rated energy if stopped early (1 energy per 5-min interval)
  const completedIntervals = Math.floor(elapsedMinutes / ENERGY_INTERVAL_MINUTES)
  const proRatedEnergy = isComplete ? currentEnergy : completedIntervals

  return {
    isActive: true,
    duration,
    elapsedMinutes,
    remainingMinutes,
    isComplete,
    isInGrace,
    isDecaying,
    graceRemaining,
    decay,
    baseEnergy,
    currentEnergy,
    proRatedEnergy,
  }
}

export function useActivity() {
  const activity = useGameStore((s) => s.activity)
  const checkActivity = useGameStore((s) => s.checkActivity)
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    if (!activity) return

    const interval = setInterval(() => {
      setNow(Date.now())
      checkActivity()
    }, 1000)

    return () => clearInterval(interval)
  }, [activity, checkActivity])

  if (!activity) {
    return {
      isActive: false,
      type: null as 'travel' | 'hunt' | null,
      elapsedMinutes: 0,
      remainingMinutes: 0,
      progress: 0,
    }
  }

  const startTime = new Date(activity.startedAt).getTime()
  const elapsedMinutes = (now - startTime) / 60000

  const totalMinutes =
    activity.type === 'travel'
      ? TRAVEL_TIME_MINUTES
      : HUNTS[activity.huntType!].timeMinutes

  const remainingMinutes = Math.max(0, totalMinutes - elapsedMinutes)
  const progress = Math.min(1, elapsedMinutes / totalMinutes)

  return {
    isActive: true,
    type: activity.type,
    elapsedMinutes,
    remainingMinutes,
    progress,
  }
}
